<template>
	<div class="products-wrapper">
		<div class="container">
			<template v-if="isMainDataLoaded">
				<Header />
				<header-banner />
				<div class="main-wrap">
					<main-navigation :disable-auto-scroll="disableCategoryAutoScroll" />

					<div id="main">
						<div
							class="products-sub-header products-sub-header--controls"
							:class="{ 'products-sub-header--right': !acceptOnlineOrder }"
						>
							<order-type-switcher />
							<switcher-with-filter class="products-header-buttons-wrapper" />
						</div>
						<div
							v-for="name in renderCategoryNames"
							:key="name"
							class="category-section"
							:id="`cat-${name}`"
							:data-name="name"
						>
							<div class="section-trigger-anchor" aria-hidden="true" />
							<products-by-category :category-name="name" />
						</div>
					</div>
					<div class="cart-widget">
						<cart
							v-if="
								acceptOnlineOrder &&
									(isTabOpened || totalProductsCount) &&
									!productId
							"
						/>
					</div>
				</div>
				<menu-footer v-if="isScreenMore480" />

				<div>
					<product-details-popup v-if="isDataLoaded" />
				</div>

				<back-to-top-button scroll-target=".main-wrap" />
			</template>
			<Loader v-if="!isDataLoaded && !hasErrors" />
		</div>
		<div
			v-if="showTriggerDebug"
			class="debug-trigger-line"
			:style="{ top: headerTriggerY + 'px' }"
		>
			<span class="debug-trigger-label">
				trigger @ {{ headerTriggerY }}px
			</span>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

// Components
import Header from '../components/Header'
import OrderTypeSwitcher from '@/components/OrderTypeSwitcher'
import Cart from '../components/Cart'
import ProductDetailsPopup from '@/components/ProductDetails/ProductDetailsPopup/ProductDetailsPopup.vue'
import Loader from '../components/Loaders/Loader.vue'
import SwitcherWithFilter from '@/components/Products/SwitcherWithFilter.vue'
import MenuFooter from '@/components/MenuFooter.vue'
import MainNavigation from '@/components/MainNavigation.vue'
import HeaderBanner from '@/components/HeaderBanner.vue'
import ProductsByCategory from './ProductsByCategory.vue'
import BackToTopButton from '@/components/BackToTopButton/BackToTopButton.vue'
// Constants
import { ORDER_TYPES, PRODUCTS_FILTER_LIST } from '@/constants'
// Utils
import storage from '@/utils/storage'
import { isEqual } from 'lodash'
import { EventBus } from '@/utils/eventBus'
// Mixins
import routerMixin from '@/mixins/routerMixin'
import pickUpValidation from '@/mixins/pickUpValidation'

const TRIGGER_OFFSET_PX = 100

export default {
	name: 'Main',

	mixins: [pickUpValidation, routerMixin],

	components: {
		HeaderBanner,
		MenuFooter,
		SwitcherWithFilter,
		Header,
		OrderTypeSwitcher,
		Cart,
		ProductDetailsPopup,
		Loader,
		MainNavigation,
		ProductsByCategory,
		BackToTopButton
	},

	mounted() {
		this.$nextTick(() => {
			window.addEventListener('scroll', this.onIntersect, { passive: true })
		})
		EventBus.$on('footer-scroll-finished', this.handleFooterScrollFinished)
	},

	beforeDestroy() {
		if (this.observer) {
			this.observer.disconnect()
			this.observer = null
		}
		if (this.autoScrollTimer) {
			clearTimeout(this.autoScrollTimer)
		}
		if (this.autoScrollEndHandler) {
			window.removeEventListener('scroll', this.autoScrollEndHandler)
			this.autoScrollEndHandler = null
		}
		EventBus.$off('footer-scroll-finished', this.handleFooterScrollFinished)
		window.removeEventListener('scroll', this.onIntersect)
	},

	data() {
		// Not for production: Expo temporary solution to pause category scroll-spy
		// while redirecting from successful Tab payment to Venue Info
		const isVenueInfoRedirect = this.$route.query.openVenueInfo === '1'

		return {
			isMainDataLoaded: false,
			hasErrors: false,
			observer: null,
			autoScrollTimer: 0,
			autoScrollEndHandler: null,
			sections: [],
			bootReady: false,
			skipInitialScrollOnce: isVenueInfoRedirect,
			disableCategoryAutoScroll: isVenueInfoRedirect,
			showTriggerDebug: false, // Line for debug category switch position
			isVenueInfoRedirectHandled: false,
			shouldRestoreAfterFooterScroll: false
		}
	},

	async created() {
		if (this.isTabNotEditable) {
			await this.goToCheck()
			return
		}

		if (this.isWaitingPos && !this.isTabFailed) {
			await this.goToProcessing()
			return
		}
		// For the go back method
		if (this.orderedCategoryNames?.length) {
			this.isMainDataLoaded = true
		}

		try {
			await this.fetchCatalog()
			if (!this.orderedCategoryNames?.length) {
				storage.removeFromStorage('lastVenueChainSubdomain')
				storage.removeFromStorage('lastVenueId')
				await this.$router.push({ name: 'notFound' })
			}
			this.updateOrderAvailability()
			if (this.acceptOnlineOrder && this.isOrderingTimeAvailable) {
				await this.callGetCart()
				if (
					this.isDesktopDevice &&
					this.cart.orderType !== ORDER_TYPES.isPickUp
				) {
					await this.setOrderTypeToPickup()
				}
			}
			if (!this.disableCategoryAutoScroll) {
				this.setInitialRoute()
			} else {
				this.bootReady = true
			}
			this.isMainDataLoaded = true
		} catch (e) {
			this.hasErrors = true
			await this.$router.push({ name: 'notFound' })
		}
	},

	computed: {
		...mapGetters('productCatalog', [
			'orderedCategoryNames',
			'nonEmptyCategoryNames',
			'categoryByName',
			'isCatalogLoading'
		]),
		...mapState('venue', ['venueInfo']),
		...mapGetters('venue', ['isOrderingTimeAvailable']),
		...mapState('ui', [
			'isDesktopDevice',
			'isNavAutoScroll',
			'isScreenMore480'
		]),
		...mapState('cart', ['isTabFailed']),
		...mapGetters('cart', [
			'isTabNotEditable',
			'isTabOpened',
			'totalProductsCount',
			'isWaitingPos'
		]),
		...mapState({
			cart: state => state.cart
		}),

		isDataLoaded() {
			return this.isMainDataLoaded && !this.isCatalogLoading
		},

		acceptOnlineOrder() {
			return this.venueInfo.acceptOnlineOrder
		},

		productsFilterList() {
			return PRODUCTS_FILTER_LIST
		},

		filter() {
			return this.$route.query.filter
		},

		productId() {
			return this.$route.query.productId
		},

		renderCategoryNames() {
			return this.nonEmptyCategoryNames.length
				? this.nonEmptyCategoryNames
				: this.orderedCategoryNames
		},
		headerTriggerY() {
			return this.getStickyOffset() + TRIGGER_OFFSET_PX
		}
	},
	watch: {
		isMainDataLoaded(val) {
			if (val) {
				if (!this.disableCategoryAutoScroll) {
					this.$nextTick(() => this.initObserver())
				}
				this.tryHandleVenueInfoRedirect()
			}
		},
		isNavAutoScroll(val) {
			val && this.markAutoScroll()
		},
		isScreenMore480() {
			this.tryHandleVenueInfoRedirect()
		},
		'$route.query.openVenueInfo'() {
			this.tryHandleVenueInfoRedirect()
		},

		'$route.query': {
			immediate: true,
			handler(newQuery) {
				const { filter, trending } = newQuery || {}
				this.syncRouteFilters({ filter, trending })
				if (!this.disableCategoryAutoScroll) {
					this.$nextTick(() => {
						this.ensureValidCategoryInRoute()
					})
				}
			}
		},

		renderCategoryNames() {
			if (this.disableCategoryAutoScroll) return

			this.$nextTick(() => {
				this.initObserver()
				this.ensureValidCategoryInRoute()
			})
		}
	},
	methods: {
		...mapActions('venue', ['setAllOrderingAvailable']),
		...mapActions('cart', ['getCart', 'updateCart']),
		...mapActions('ui', ['stopNavAutoScroll', 'startNavAutoScroll']),
		...mapActions('productCatalog', ['fetchCatalog', 'syncRouteFilters']),

		// Not for production: Expo temporary redirect flow
		async tryHandleVenueInfoRedirect() {
			if (!this.isMainDataLoaded || this.isVenueInfoRedirectHandled) {
				return
			}

			if (this.$route.query.openVenueInfo !== '1') {
				return
			}

			this.isVenueInfoRedirectHandled = true

			await this.$nextTick()

			if (this.isScreenMore480) {
				this.shouldRestoreAfterFooterScroll = true
				EventBus.$emit('open-footer', '#footerAddress')
			} else {
				EventBus.$emit('open-mobile-venue-info')
			}

			await this.clearVenueInfoRedirectQuery()

			if (!this.isScreenMore480) {
				await this.restoreCategoryAutoScroll()
			}
		},

		async handleFooterScrollFinished() {
			// Not for production: Expo temporary hook to restore scroll-spy only
			// after footer smooth scroll has fully settled
			if (!this.shouldRestoreAfterFooterScroll) {
				return
			}

			this.shouldRestoreAfterFooterScroll = false
			await this.restoreCategoryAutoScroll()
		},

		async restoreCategoryAutoScroll() {
			// Not for production: Expo temporary re-enable sequence for category
			// observer/router sync after forced footer redirect flow
			if (!this.disableCategoryAutoScroll) {
				return
			}

			this.disableCategoryAutoScroll = false
			this.skipInitialScrollOnce = true

			await this.$nextTick()
			this.initObserver()
			this.ensureValidCategoryInRoute()
		},

		async clearVenueInfoRedirectQuery() {
			const query = { ...this.$route.query }

			if (!query.openVenueInfo) {
				return
			}

			delete query.openVenueInfo

			await this.$router
				.replace({
					name: this.$route.name,
					params: { ...this.$route.params },
					query
				})
				.catch(() => {})
		},

		setInitialRoute() {
			const catNames = this.orderedCategoryNames || []
			if (catNames.length === 0) return
			const { query, params } = this.$route
			const currentCat = params.category
			const resolved = this.resolveInitialCategory(catNames, currentCat)
			if (!resolved) return
			const { name: categoryName, skipScroll } = resolved
			this.applyCategoryToRoute({ query, params, categoryName, skipScroll })
		},

		resolveInitialCategory(categoryNames, currentCategory) {
			if (!currentCategory) {
				return {
					name: this.getFirstNonEmptyCategoryName(categoryNames),
					skipScroll: true
				}
			}
			if (categoryNames.includes(currentCategory)) {
				return { name: currentCategory, skipScroll: false }
			}
			const ciMatch = this.findCaseInsensitiveMatch(
				categoryNames,
				currentCategory
			)
			if (ciMatch) {
				return { name: ciMatch, skipScroll: false }
			}
			return {
				name: this.getFirstNonEmptyCategoryName(categoryNames),
				skipScroll: true
			}
		},

		getFirstNonEmptyCategoryName(categoryNames) {
			return (
				categoryNames.find(categoryName => {
					const category = this.categoryByName(categoryName)
					return (category?.numProducts ?? 0) > 0
				}) || categoryNames[0]
			)
		},

		findCaseInsensitiveMatch(categoryNames, currentCategory) {
			const needle = String(currentCategory || '').toLowerCase()
			if (!needle) return null
			return (
				categoryNames.find(name => name && name.toLowerCase() === needle) ||
				null
			)
		},

		applyCategoryToRoute({ query, params, categoryName, skipScroll = false }) {
			if (skipScroll) this.skipInitialScrollOnce = true
			this.setDefaultCategory(query, params, categoryName)
		},

		async setDefaultCategory(query, params, categoryName) {
			const productId =
				(query && query.productId) ??
				(this.$route.query && this.$route.query.productId)
			const newQuery = {
				filter: (query && query.filter) || this.productsFilterList[0].value,
				trending: (query && query.trending) || 0,
				...(productId != null ? { productId } : {})
			}
			const targetName = 'products-category'
			const sameName = this.$route.name === targetName
			const sameQuery = isEqual(this.$route.query || {}, newQuery)
			const sameCategory =
				(this.$route.params && this.$route.params.category) === categoryName
			if (sameName && sameQuery && sameCategory) return
			await this.$router.replace({
				name: targetName,
				params: { ...params, category: categoryName },
				query: newQuery
			})
		},

		updateOrderAvailability() {
			this.setAllOrderingAvailable()
		},

		async callGetCart() {
			try {
				await this.getCart()
			} catch (e) {}
		},

		async setOrderTypeToPickup() {
			this.updateOrderAvailability()
			const payload = {
				orderType: ORDER_TYPES.isPickUp,
				isPickUp: true,
				isInVenue: false
			}
			if (this.cart.cartItems?.length) {
				payload.isNeedShowExpiredPopup = true
			}
			await this.updateCart(payload)
		},

		initObserver() {
			if (this.disableCategoryAutoScroll) {
				this.bootReady = true

				return
			}

			if (this.observer) {
				this.observer.disconnect()
				this.observer = null
			}
			const urlCategory = this.$route.params?.category
			const topOffset = this.getStickyOffset()
			this.observer = new IntersectionObserver(this.onIntersect, {
				root: null,
				rootMargin: `-${topOffset}px 0px 0px 0px`
			})
			const nodes = document.querySelectorAll('.category-section')
			this.sections = Array.from(nodes)
			this.sections.forEach(el => this.observer.observe(el))
			this.scrollToUrlCategoryOnce(
				topOffset,
				!!this.isFirstCategory(urlCategory)
			)
		},

		isFirstCategory(name) {
			return name === this.renderCategoryNames[0]
		},

		scrollToUrlCategoryOnce(topOffset, forceScrollSkip = false) {
			const name = this.$route.params?.category
			if (
				this.disableCategoryAutoScroll ||
				this.skipInitialScrollOnce ||
				!name ||
				forceScrollSkip
			) {
				this.bootReady = true
				this.skipInitialScrollOnce = false
				return
			}
			const el = document.getElementById(`cat-${name}`)
			if (!el) {
				requestAnimationFrame(() => this.scrollToUrlCategoryOnce(topOffset))
				return
			}
			window.scrollTo({
				top: el.offsetTop + topOffset,
				behavior: 'auto'
			})
			requestAnimationFrame(() => {
				this.bootReady = true
				setTimeout(() => this.startNavAutoScroll(), 100)
			})
		},

		onIntersect() {
			if (this.disableCategoryAutoScroll) return
			if (!this.bootReady || this.isNavAutoScroll) return
			const triggerY = this.getStickyOffset() + TRIGGER_OFFSET_PX
			let activeName = null
			for (const sectionEl of this.sections) {
				const name = sectionEl.dataset?.name
				if (!name) continue
				const anchor =
					sectionEl.querySelector('.section-trigger-anchor') || sectionEl
				const top = Math.round(anchor.getBoundingClientRect().top)
				if (top <= triggerY) {
					activeName = name
				} else {
					break
				}
			}
			if (!activeName || activeName === this.$route.params.category) return
			this.$router.replace({
				name: 'products-category',
				params: { ...this.$route.params, category: activeName },
				query: { ...this.$route.query }
			})
		},

		markAutoScroll() {
			if (this.autoScrollTimer) {
				clearTimeout(this.autoScrollTimer)
			}
			if (this.autoScrollEndHandler) {
				window.removeEventListener('scroll', this.autoScrollEndHandler)
			}

			this.autoScrollEndHandler = () => {
				if (this.autoScrollTimer) {
					clearTimeout(this.autoScrollTimer)
				}
				this.autoScrollTimer = setTimeout(() => {
					this.stopNavAutoScroll()
					window.removeEventListener('scroll', this.autoScrollEndHandler)
					this.autoScrollEndHandler = null
				}, 160)
			}
			window.addEventListener('scroll', this.autoScrollEndHandler, {
				passive: true
			})
			this.autoScrollEndHandler()
		},

		ensureValidCategoryInRoute() {
			if (this.disableCategoryAutoScroll) return

			const names = this.renderCategoryNames || []
			const current = this.$route.params && this.$route.params.category
			if (!names.length) return
			if (!names.includes(current)) {
				this.applyCategoryToRoute({
					query: { ...this.$route.query },
					params: { ...this.$route.params },
					categoryName: names[0],
					skipScroll: true
				})
			}
		},

		getNodeHeight(selector) {
			const node = document.querySelector(selector)
			return node ? Math.round(node.getBoundingClientRect().height) : 0
		},

		getStickyOffset() {
			const headerH = this.getNodeHeight('.header')
			const bottomH = this.getNodeHeight('.header-bottom')
			const sum = headerH + bottomH
			const FALLBACK = 150
			return sum > 0 ? sum : FALLBACK
		}
	}
}
</script>

<style lang="scss" scoped>
.products-wrapper {
	padding-bottom: 0;

	@media (max-width: 480px) {
		padding-bottom: 24px;
	}

	.main-wrap {
		position: relative;
		z-index: 99;
		width: 100%;

		@media (max-width: 1024px) {
			z-index: initial;
		}
	}

	#main {
		padding: 26px 0 0;
		@media (max-width: 767px) {
			padding: 24px 0 0;
		}
	}
}

.products-sub-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-shrink: 0;
	padding: 0 0 36px;

	&--right {
		justify-content: flex-end;
	}

	/deep/ {
		.time-picker {
			top: calc(100% + 15px);
			bottom: auto;
		}
	}

	@media (max-width: 767px) {
		padding: 0 0 24px;
	}
}

.products-holder {
	@media (max-width: 767px) {
		padding: 0 8px;
		margin: 0 -8px;
		position: relative;
	}
}

.product-badge {
	@media (max-width: 767px) {
		padding: 4px 7px;
		font-size: 12px;
	}
}
.category-section {
	scroll-margin-top: 80px;
	padding-bottom: 20px;
}
.debug-trigger-line {
	position: fixed;
	left: 0;
	right: 0;
	height: 0;
	border-top: 2px dashed rgba(255, 0, 0, 0.8);
	z-index: 9999;
	pointer-events: none;

	.debug-trigger-label {
		position: absolute;
		right: 8px;
		top: -14px;
		padding: 2px 6px;
		font-size: 11px;
		line-height: 1;
		color: #fff;
		background: rgba(255, 0, 0, 0.8);
		border-radius: 3px;
	}
}
.section-trigger-anchor {
	position: relative;
	top: 0;
	height: 0;
}
</style>
